<template>
  <div
    class="integration-card mollie-integration-card"
    :class="{
      'integration-card--connected': isConnected,
      'integration-card--warning': isConnected && !isReady,
      'integration-card--error': needsReauth,
    }"
    @click="handleOpen"
  >
    <div class="integration-card__content">
      <div class="integration-card__icon">
        <div class="integration-card__icon-inner">
          <img :src="mollieIcon" :alt="$t('mollie.name')" />
        </div>
      </div>
      <div class="integration-card__info">
        <h3 class="integration-card__title">
          {{ $t('mollie.name') }}
          <span v-if="isTestMode" class="integration-card__badge">{{ $t('mollie.badge_test') }}</span>
        </h3>
        <p class="integration-card__description">
          {{ $t('mollie.description') }}
        </p>
        <p v-if="bannerMessage" class="integration-card__hint" :class="`integration-card__hint--${bannerLevel}`">
          {{ bannerMessage }}
        </p>
      </div>
      <div class="integration-card__actions">
        <div class="integration-card__status" :class="statusBadgeClass">
          {{ statusLabel }}
        </div>
        <Btn
          :variant="primaryVariant"
          size="sm"
          :loading="loading"
          :disabled="!licenseAvailable"
          @click.stop="handlePrimaryAction"
        >
          {{ primaryLabel }}
        </Btn>
      </div>
    </div>
    <div class="integration-card__meta" v-if="isConnected && profileName">
      {{ profileName }}
    </div>
    <div class="integration-card__meta" v-else-if="!licenseAvailable">
      {{ $t('mollie.license_required_title') }}
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'MollieIntegrationCard',

    components: { Btn },

    props: {
      status: { type: Object, default: null },
      licenseAvailable: { type: Boolean, default: false },
      loading: { type: Boolean, default: false },
    },

    emits: ['connect', 'manage'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const mollieIcon = new URL('../../assets/img/integrations/mollie.png', import.meta.url).href;

      const isConnected = computed(() => !!props.status?.connected);
      const needsReauth = computed(() => props.status?.reason === 'MOLLIE_REAUTH_REQUIRED');
      const isTestMode = computed(() => props.status?.mode === 'test');
      const profileName = computed(
        () => props.status?.profile_name || props.status?.mollie_organization?.name || null
      );

      const onboardingComplete = computed(
        () => props.status?.onboarding?.status === 'completed'
      );
      const canAcceptLivePayments = computed(
        () => !!props.status?.can_accept_live_payments
      );

      const isReady = computed(
        () => isConnected.value && onboardingComplete.value && canAcceptLivePayments.value
      );

      const statusLabel = computed(() => {
        if (needsReauth.value) return 'Actie vereist';
        if (isConnected.value) return 'Verbonden';
        return 'Niet verbonden';
      });

      const statusBadgeClass = computed(() => {
        if (needsReauth.value) return 'integration-card__status--error';
        if (isConnected.value && !isReady.value) return 'integration-card__status--warning';
        if (isConnected.value) return 'integration-card__status--success';
        return 'integration-card__status--idle';
      });

      const bannerLevel = computed(() => {
        if (needsReauth.value) return 'error';
        if (isConnected.value && !onboardingComplete.value) return 'warning';
        if (isConnected.value && !canAcceptLivePayments.value) return 'warning';
        if (!props.licenseAvailable) return 'info';
        return null;
      });

      const bannerMessage = computed(() => {
        if (needsReauth.value) return $t('mollie.reasons.reauth_required');
        if (isConnected.value && !onboardingComplete.value) {
          return $t('mollie.status.onboarding_incomplete_description');
        }
        if (isConnected.value && !canAcceptLivePayments.value) {
          return $t('mollie.status.kyc_pending_description');
        }
        if (!props.licenseAvailable) return $t('mollie.license_required');
        return null;
      });

      const primaryLabel = computed(() => {
        if (needsReauth.value) return $t('mollie.actions.reconnect');
        if (isConnected.value) return $t('mollie.actions.manage');
        return $t('mollie.actions.connect');
      });

      const primaryVariant = computed(() => {
        if (needsReauth.value) return 'danger';
        if (isConnected.value) return 'tertiary';
        return 'primary';
      });

      const handleOpen = () => {
        if (!props.licenseAvailable) return;
        if (isConnected.value) emit('manage');
        else emit('connect');
      };

      const handlePrimaryAction = () => {
        if (!props.licenseAvailable) return;
        if (isConnected.value) emit('manage');
        else emit('connect');
      };

      return {
        mollieIcon,
        isConnected,
        needsReauth,
        isTestMode,
        profileName,
        isReady,
        statusLabel,
        statusBadgeClass,
        bannerMessage,
        bannerLevel,
        primaryLabel,
        primaryVariant,
        handleOpen,
        handlePrimaryAction,
      };
    },
  };
</script>

<style scoped lang="scss">
  .integration-card {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s ease;

    @include hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    &__content {
      display: flex;
      align-items: flex-start;
      gap: 16px;
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: 1px solid var(--slate-200);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: var(--white);
    }

    &__icon-inner {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 28px;
        height: 28px;
      }
    }

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0 0 4px 0;
      color: var(--slate-900);
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__badge {
      font-size: 0.625rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      background: var(--amber-100);
      color: var(--amber-800);
      text-transform: uppercase;
    }

    &__description {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &__hint {
      margin: 0.5rem 0 0 0;
      font-size: 0.75rem;
      line-height: 1.4;

      &--info {
        color: var(--blue-700);
      }

      &--warning {
        color: var(--amber-800);
      }

      &--error {
        color: var(--red-700);
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;
      flex-shrink: 0;
    }

    &__status {
      font-size: 14px;
      padding: 6px 10px;
      border-radius: 2rem;
      color: var(--slate-700);
      background-color: var(--slate-200);
      white-space: nowrap;

      &--success {
        color: var(--green-700);
        background-color: var(--green-100);
      }

      &--warning {
        color: var(--amber-800);
        background-color: var(--amber-100);
      }

      &--error {
        color: var(--red-700);
        background-color: var(--red-100);
      }

      &--idle {
        color: var(--slate-700);
        background-color: var(--slate-200);
      }
    }

    &__meta {
      margin-top: 8px;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &--connected {
      border-color: var(--green-200);
    }

    &--warning {
      border-color: var(--amber-200);
    }

    &--error {
      border-color: var(--red-200);
    }
  }
</style>
